import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { literatureAPI } from '../../api'
import type { ProductLiteratureFormData } from '../../types'

// Query key factory for literature
export const literatureKeys = {
  all: ['literature'] as const,
  lists: () => [...literatureKeys.all, 'list'] as const,
  list: (filters: LiteratureFilters) => [...literatureKeys.lists(), filters] as const,
  public: () => [...literatureKeys.all, 'public'] as const,
}

export interface LiteratureFilters {
  page?: number
  perPage?: number
  search?: string
}

// Fetch literature list (admin)
export function useLiterature(filters: LiteratureFilters = {}) {
  const { page = 1, perPage = 20, search = '' } = filters

  return useQuery({
    queryKey: literatureKeys.list({ page, perPage, search }),
    queryFn: () => literatureAPI.getLiterature(page, perPage, search),
  })
}

// Fetch public literature (no auth)
export function usePublicLiterature(filters?: {
  manufacturer_id?: number
  category_id?: number
  product_id?: number
}) {
  return useQuery({
    queryKey: [...literatureKeys.public(), filters],
    queryFn: () => literatureAPI.getPublicLiterature(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Fetch all literature applicable to a specific product (no auth), resolves all 4 scopes
export function usePublicLiteratureForProduct(productId: number | null) {
  return useQuery({
    queryKey: [...literatureKeys.public(), 'for-product', productId],
    queryFn: () => literatureAPI.getLiteratureForProduct(productId!),
    enabled: productId !== null,
    staleTime: 5 * 60 * 1000,
  })
}

// Create literature mutation
export function useCreateLiterature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ProductLiteratureFormData) => literatureAPI.createLiterature(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: literatureKeys.lists() })
      queryClient.invalidateQueries({ queryKey: literatureKeys.public() })
    },
  })
}

// Update literature mutation
export function useUpdateLiterature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ProductLiteratureFormData> }) =>
      literatureAPI.updateLiterature(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: literatureKeys.lists() })
      queryClient.invalidateQueries({ queryKey: literatureKeys.public() })
    },
  })
}

// Delete literature mutation
export function useDeleteLiterature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => literatureAPI.deleteLiterature(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: literatureKeys.lists() })
      queryClient.invalidateQueries({ queryKey: literatureKeys.public() })
    },
  })
}
